import React, { useEffect, useState } from "react";
import { submitSpecialOffer } from "../../services/specialOfferService";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock3, Headphones, MapPin, PhoneCall, ShieldCheck } from "lucide-react";
import SEO from "../../components/SEO";
import saleAd from "../../assets/special-offer/sym-sale-ad.png";
import { offerData } from "../../components/specialOffer/offerData";
import useSpecialOfferCampaign from "../../components/specialOffer/useSpecialOfferCampaign";
import { SaleMachineCards } from "../../components/specialOffer/SaleMachineCards";

import yellowCard from "../../assets/special-offer/offer-yellow-card.webp";
import whySymMachine from "../../assets/special-offer/why-sym-machine.webp";
import "./specialOffer.css";

function MachineCard({ machine }) {
  return (
    <article className="sym-offer-machine">
      <Link to={machine.url} className="sym-offer-machine__image-link" aria-label={`View ${machine.name} details`}>
        <img src={machine.image} alt={machine.alt} loading="lazy" />
      </Link>
      <div className="sym-offer-machine__details">
        <div className="sym-offer-machine__model">{machine.name}</div>
        <p>{machine.description}</p>
        <div className="sym-offer-machine__actions">
          <Link to={machine.url}>View product details <ArrowRight size={17} aria-hidden="true" /></Link>
          <a href="#sale-enquiry">Get Sale Pricing</a>
        </div>
      </div>
    </article>
  );
}

function OfferEnquiryForm({ machines = [], selectedOffer, settings = {} }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", machine: "", postcode: "", previousPurchase: "", consent: false, website: "", offerId: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const update = (key, value) => setForm(previous => ({ ...previous, [key]: value }));
  useEffect(() => {
    if (selectedOffer) setForm(previous => ({ ...previous, machine: selectedOffer.machineModel, offerId: selectedOffer._id }));
  }, [selectedOffer]);
  const pickMachine = value => {
    const matching = machines.find(machine => machine._id === value);
    setForm(previous => ({ ...previous, machine: matching ? matching.machineModel : value, offerId: matching ? matching._id : '' }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const response = await submitSpecialOffer({ ...form, name: form.name.trim(), email: form.email.trim().toLowerCase(), phone: form.phone.trim(), postcode: form.postcode.trim() });
      if (response.data?.success === false) throw new Error(response.data?.message || "Submission failed");
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Unable to send your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return <div className="sym-offer-form sym-offer-form__thankyou" role="status" aria-live="polite">
      <CheckCircle2 size={48} color="#1c8b49" />
      <h3>Thank you for your enquiry!</h3>
      <p>We've received your request. The SYM team will contact you to discuss the current offer.</p>
      <a href={settings.phoneHref || offerData.phoneHref} className="sym-offer-button sym-offer-button--outline"><PhoneCall size={18} /> Call SYM · {settings.phoneDisplay || offerData.phoneDisplay}</a>
    </div>;
  }

  return <form className="sym-offer-form" onSubmit={submit} aria-label="Special offer enquiry form">
    <p className="sym-offer-form__intro">{settings.enquiryFormIntro || "Complete the form and a SYM team member can contact you about sale pricing."}</p>
    <div className="sym-offer-form__grid">
      <label>Full name *<input type="text" autoComplete="name" maxLength={120} placeholder="Your name" value={form.name} onChange={e => update("name", e.target.value)} required minLength={2} /></label>
      <label>Email address *<input type="email" autoComplete="email" placeholder="you@example.com" maxLength={254} value={form.email} onChange={e => update("email", e.target.value)} required /></label>
      <label>Mobile number *<input type="tel" autoComplete="tel" inputMode="tel" placeholder="Your mobile number" value={form.phone} onChange={e => update("phone", e.target.value)} required maxLength={22} /></label>
      <label>Machine / offer *<select value={form.offerId || form.machine} onChange={e => pickMachine(e.target.value)} required>
        <option value="">Select a machine</option>
        <option value="TerrainX35">TerrainX35 · First-purchase offer</option>
        <option value="Titan35">Titan35 · First-purchase offer</option>
        {machines.map(machine => <option value={machine._id} key={machine._id}>{machine.machineModel} · {machine.title}</option>)}
        <option value="Not sure">Not sure</option>
      </select></label>
      <label>Postcode *<input type="text" autoComplete="postal-code" inputMode="numeric" placeholder="4-digit Australian postcode" pattern="[0-9]{4}" maxLength={4} value={form.postcode} onChange={e => update("postcode", e.target.value)} required /></label>
    </div>
    <label style={{display:'block', marginTop:16}}>Additional details (optional)
      <textarea rows="3" maxLength={1200} value={form.message} onChange={e => update('message', e.target.value)} placeholder="Ask about upgrades, lift height, delivery or this deal" style={{display:'block',width:'100%',marginTop:8,padding:12,border:'1px solid #d4d4d4',borderRadius:8,color:'#222'}} />
    </label>
    <fieldset className="sym-offer-form__fieldset">
      <legend>Have you bought from SYM before? *</legend>
      <label><input type="radio" name="previousPurchase" value="Yes" required checked={form.previousPurchase === "Yes"} onChange={() => update("previousPurchase", "Yes")} /> Yes</label>
      <label><input type="radio" name="previousPurchase" value="No" required checked={form.previousPurchase === "No"} onChange={() => update("previousPurchase", "No")} /> No</label>
    </fieldset>
    <label className="sym-offer-form__consent"><input type="checkbox" checked={form.consent} onChange={e => update("consent", e.target.checked)} required /> I consent to SYM Machinery contacting me about this enquiry. *</label>
    <div aria-hidden="true" className="sym-offer-form__honeypot"><label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={e => update("website", e.target.value)} /></label></div>
    {error && <p className="sym-offer-form__error" role="alert">{error}</p>}
    <button type="submit" className="sym-offer-button sym-offer-button--dark sym-offer-form__submit" disabled={loading}>{loading ? "Sending your enquiry…" : (settings.enquiryFormButton || "Get Sale Pricing")}</button>
    <p className="sym-offer-form__note">Offer conditions apply. A SYM team member will confirm your eligibility and current pricing.</p>
  </form>;
}

export default function SpecialOfferPage() {
  const { settings: savedSettings, machines } = useSpecialOfferCampaign();
  const settings = savedSettings || {};
  const [selectedOffer, setSelectedOffer] = useState(null);
  const enquire = machine => {
    setSelectedOffer(machine);
    document.getElementById('sale-enquiry')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  useEffect(() => {
    if (!machines.length || !window.location.hash.startsWith('#offer-')) return;
    const timer = setTimeout(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ block: 'start' }), 150);
    return () => clearTimeout(timer);
  }, [machines]);
  return (
    <div className="sym-offer-page">
      <SEO
        title="Special Offer | Sale Machines | SYM Machinery"
        description="Browse SYM Machinery offers, first-purchase promotions and custom 4x4 forklifts on sale. Enquire for availability and pricing."
        keywords="SYM Machinery special offer, TerrainX35, Titan35, rough terrain forklifts Australia"
        image="https://symforklift.nyc3.cdn.digitaloceanspaces.com/logo.webp"
        url="/special-offer"
      />
      <div className="sym-offer-hero">
        <div className="sym-offer-container">
          <div className="sym-offer-hero__topline"><span className="sym-offer-wordmark">SYM <em>Machinery</em></span><span className="sym-offer-pill"><Clock3 size={18} /> {settings.heroBadge || "Limited time only"}</span></div>
          <div className="sym-offer-hero__body">
            <div className="sym-offer-eyebrow">{settings.heroEyebrow || "TerrainX35 or Titan35 · First purchase offer"}</div>
            <h1>{settings.heroTitle || offerData.heroTitle}</h1>
            <p>{settings.heroDescription || "Your pick of two machines. Contact the SYM team for sale pricing and offer details."}</p>
            <div className="sym-offer-actions">
              <a href="#sale-enquiry" className="sym-offer-button sym-offer-button--dark">Get Sale Pricing <ArrowRight size={20}/></a>
              <a href={settings.phoneHref || offerData.phoneHref} className="sym-offer-button sym-offer-button--outline"><PhoneCall size={20}/> Call SYM · {settings.phoneDisplay || offerData.phoneDisplay}</a>
            </div>
          </div>
        </div>
      </div>
      <main>
        {machines.length > 0 && <section className="sym-offer-machines-section" id="sale-machines" aria-label="Special sale machines">
          <div className="sym-offer-container">
            <div className="sym-offer-section-heading"><span className="sym-offer-eyebrow">Custom machines on sale</span><h2>Limited-quantity machine deals</h2><p>See the full configuration, discount and optional upgrades. Confirm availability and pricing with SYM.</p></div>
            <SaleMachineCards machines={machines} onEnquire={enquire} />
          </div>
        </section>}
        {settings.showLegacyCampaign !== false && <section
  className="sym-offer-machines-section"
  aria-labelledby="offer-machines-title"
>
  <div className="sym-offer-container">

    <div className="sym-offer-section-heading">
      <span className="sym-offer-eyebrow">
        {settings.campaignEyebrow || "SYM Machinery · Exclusive Offer"}
      </span>

      <h2 id="offer-machines-title">{settings.campaignTitle || "Two Machines. One Special Offer."}</h2>

      <p>
        {settings.campaignDescription || "Discover our exclusive first-purchase savings and see what makes SYM Machinery different."}
      </p>
    </div>

    <div className="sym-campaign-grid">

      {/* LEFT CARD - YELLOW OFFER */}
      <article className="sym-campaign-card">
        <a href="#sale-enquiry" className="sym-campaign-image">
          <img
            src={settings.campaignCardImage || yellowCard}
            alt="SYM Machinery Save Up to $15,000 Offer"
            loading="lazy"
          />
        </a>

        <div className="sym-campaign-content">
          <span className="sym-campaign-label">
            {settings.campaignCardBadge || "LIMITED TIME OFFER"}
          </span>

          <h3>{settings.campaignCardTitle || "Claim Your Exclusive Offer"}</h3>

          <p>
            {settings.campaignCardDescription || "Save up to $15,000 on your first eligible SYM Machinery purchase. Conditions apply."}
          </p>

          <a href="#sale-enquiry" className="sym-campaign-btn">
            {settings.campaignCardButton || "Claim Your Offer"} →
          </a>
        </div>
      </article>

      {/* RIGHT CARD - WHY SYM */}
      <article className="sym-campaign-card">
        <a href="#sym-why-section" className="sym-campaign-image">
          <img
            src={settings.whyCardImage || whySymMachine}
            alt="Why SYM Machinery Is Better"
            loading="lazy"
          />
        </a>

        <div className="sym-campaign-content">
          <span className="sym-campaign-label">
            {settings.whyCardBadge || "THE SYM ADVANTAGE"}
          </span>

          <h3>{settings.whyCardTitle || "Discover Why SYM Is Better"}</h3>

          <p>
            {settings.whyCardDescription || "Explore the machinery features, build quality and practical advantages behind SYM."}
          </p>

          <a
            href="#sym-why-section"
            className="sym-campaign-btn"
          >
            {settings.whyCardButton || "Explore SYM Advantages"} →
          </a>
        </div>
      </article>

    </div>
  </div>
</section>}

        <section className="sym-offer-why" aria-labelledby="offer-why-title" id="sym-why-section">
          <div className="sym-offer-container sym-offer-why__inner">
            <div><span className="sym-offer-eyebrow">Why choose SYM?</span><h2 id="offer-why-title">{settings.whyTitle || "Machinery built around the job."}</h2><p>{settings.whyDescription || "Explore the machines, discuss your requirements and speak directly with the SYM team before making your purchase decision."}</p></div>
            <div className="sym-offer-why__points">
              <div><CheckCircle2/><span>{settings.whyPoint1 || "TerrainX35: 4x4 rough-terrain forklift and front loader in one"}</span></div>
              <div><ShieldCheck/><span>{settings.whyPoint2 || "Titan35: enclosed cab, camera monitor and joystick controls"}</span></div>
              <div><Headphones/><span>{settings.whyPoint3 || "Speak with a SYM team member about machine selection and the current offer"}</span></div>
            </div>
          </div>
        </section>
        <section className="sym-offer-enquiry" id="sale-enquiry" aria-labelledby="sale-enquiry-title">
          <div className="sym-offer-container sym-offer-enquiry__inner">
            <div className="sym-offer-enquiry__intro"><span className="sym-offer-eyebrow">Talk to SYM</span><h2 id="sale-enquiry-title">{settings.enquiryTitle || "Get Sale Pricing"}</h2><p>{settings.enquiryDescription || "Interested in a TerrainX35 or Titan35? The SYM team can talk you through the offer and next steps."}</p><a className="sym-offer-enquiry__phone" href={settings.phoneHref || offerData.phoneHref}><PhoneCall size={21}/> {settings.phoneDisplay || offerData.phoneDisplay}</a><p className="sym-offer-enquiry__small"><MapPin size={16}/> Australian enquiries · SYM Machinery</p></div>
            <OfferEnquiryForm machines={machines} selectedOffer={selectedOffer} settings={settings} />
          </div>
        </section>
        <section className="sym-offer-final">
          <div className="sym-offer-container sym-offer-final__inner">
            <div><h2>{settings.finalTitle || "Prefer to speak with someone?"}</h2><p>{settings.finalDescription || "Call a SYM team member today to ask about the limited-time first-purchase offer."}</p></div>
            <a href={settings.phoneHref || offerData.phoneHref} className="sym-offer-button sym-offer-button--yellow"><PhoneCall size={20}/> Call {settings.phoneDisplay || offerData.phoneDisplay}</a>
          </div>
        </section>
        <section className="sym-offer-conditions"><div className="sym-offer-container"><p>{settings.disclaimer || offerData.disclaimer}</p><p>Head office contact: <a href={settings.phoneHref || offerData.phoneHref}>{settings.phoneDisplay || offerData.phoneDisplay}</a> · <a href="https://symforklifts.com">symforklifts.com</a></p><a className="sym-offer-ad-link" href="#sale-enquiry" aria-label="Go to offer enquiry section"><img src={settings.homeImage || saleAd} alt="SYM Machinery TerrainX35 and Titan35 special-offer advertisement" loading="lazy"/><span>View the campaign artwork</span></a></div></section>
      </main>
    </div>
  );
}
